import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { exportDownloadUrl } from '../../api/client';
import { useAllFindings, useCreateExport, useExports } from '../../api/queries';
import type { ExportFormat, ExportRecord, Finding } from '../../api/types';
import { Field, MutationError } from '../../components/Form';
import { EmptyState, QueryState } from '../../components/States';
import { StatusBadge } from '../../components/StatusBadge';
import { formatDate, formatDateTime, plural } from '../../lib/format';
import { StewardNav } from './StewardNav';

const FORMATS: { value: ExportFormat; label: string; help: string }[] = [
  { value: 'pptx', label: 'Editable presentation (PPTX)', help: 'For a room. Evidence notes go in speaker notes.' },
  { value: 'pdf', label: 'PDF', help: 'For straightforward reading and sharing with invited members.' },
  { value: 'md', label: 'Markdown companion', help: 'The full written account with every citation and limit.' },
];

const STALE_CONFLICT = 'The evidence changed while the export was being generated. Wait for the latest review to settle, then try again.';

/** Only current, reviewed versions without a pending review flag can be exported. */
export function isExportable(finding: Finding): boolean {
  return finding.status === 'reviewed' && !finding.needsReview;
}

function latestCutoff(findings: Finding[]): string | null {
  return findings.reduce<string | null>((latest, finding) => (latest === null || finding.sourceCutoff > latest ? finding.sourceCutoff : latest), null);
}

function Composer({ findings }: { findings: Finding[] }) {
  const create = useCreateExport();
  const exportable = findings.filter(isExportable);
  const blocked = findings.filter((finding) => finding.status === 'reviewed' && finding.needsReview);
  const [selected, setSelected] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [format, setFormat] = useState<ExportFormat>('md');
  const [errors, setErrors] = useState<{ title?: string; selection?: string }>({});
  const [queued, setQueued] = useState<string | null>(null);

  const chosen = exportable.filter((finding) => selected.includes(finding.id));

  function submit(event: FormEvent) {
    event.preventDefault();
    const next: typeof errors = {};
    if (title.trim() === '') next.title = 'Give the export a title.';
    if (chosen.length === 0) next.selection = 'Choose at least one reviewed finding.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setQueued(null);
    create.mutate({ title: title.trim(), format, findingIds: chosen.map((finding) => finding.id) }, { onSuccess: () => setQueued(title.trim()) });
  }

  if (exportable.length === 0) {
    return (
      <EmptyState title="Nothing can be exported yet">
        <p>
          Exports only include findings a steward has reviewed, at their current version.{' '}
          {blocked.length > 0 &&
            `${plural(blocked.length, 'reviewed finding')} ${blocked.length === 1 ? 'is' : 'are'} flagged and must be reviewed again first. `}
          <Link to="/steward">Open the review queue</Link>.
        </p>
      </EmptyState>
    );
  }

  return (
    <form className="composer" onSubmit={submit} noValidate aria-labelledby="composer-title">
      <h2 id="composer-title" className="section-title">
        Compose an export
      </h2>
      <fieldset className="composer__findings" aria-describedby={errors.selection ? 'selection-error' : undefined}>
        <legend className="field__label">Reviewed findings ({exportable.length})</legend>
        {errors.selection && (
          <p className="field__error" id="selection-error">
            {errors.selection}
          </p>
        )}
        <ul className="pick-list">
          {exportable.map((finding) => (
            <li key={finding.id}>
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={selected.includes(finding.id)}
                  onChange={() =>
                    setSelected(selected.includes(finding.id) ? selected.filter((id) => id !== finding.id) : [...selected, finding.id])
                  }
                />
                <span>
                  {finding.title} <span className="muted small">v{finding.version}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
        {blocked.length > 0 && (
          <p className="muted small">Not available: {blocked.map((finding) => finding.title).join('; ')} — flagged for review after new material.</p>
        )}
      </fieldset>
      <div className="composer__settings">
        <Field label="Title" error={errors.title ?? null}>
          {(props) => <input {...props} className="input" value={title} onChange={(event) => setTitle(event.target.value)} />}
        </Field>
        <fieldset className="choice-group">
          <legend className="field__label">Format</legend>
          {FORMATS.map((option) => (
            <label key={option.value} className={`choice${format === option.value ? ' choice--selected' : ''}`}>
              <input type="radio" name="export-format" checked={format === option.value} onChange={() => setFormat(option.value)} />
              <span className="choice__text">
                <span className="choice__label">{option.label}</span>
                <span className="choice__help">{option.help}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <div className="manifest" aria-live="polite">
          <p className="minor-title">Manifest preview</p>
          {chosen.length === 0 ? (
            <p className="muted small">Pinned versions appear here as you choose findings.</p>
          ) : (
            <>
              <ul className="manifest__list">
                {chosen.map((finding) => (
                  <li key={finding.id}>
                    <span className="mono">{finding.id}</span> @ v{finding.version}
                  </li>
                ))}
              </ul>
              <p className="small">Sources up to {formatDate(latestCutoff(chosen))}. Access and attribution are rechecked at download.</p>
            </>
          )}
        </div>
        <MutationError error={create.error} conflictMessage={STALE_CONFLICT} />
        {queued !== null && (
          <p className="form-success" role="status">
            “{queued}” was requested. It appears below when generated.
          </p>
        )}
        <button type="submit" className="button button--primary" disabled={create.isPending}>
          {create.isPending ? 'Requesting…' : 'Generate export'}
        </button>
      </div>
    </form>
  );
}

/**
 * A stale export no longer matches the current research or someone's
 * attribution preference: its download is withheld and a fresh export can be
 * generated from the findings that are still reviewed and unflagged.
 */
function ExportRow({ record, exportable }: { record: ExportRecord; exportable: Set<string> }) {
  const regenerate = useCreateExport();
  const [regenerated, setRegenerated] = useState(false);
  const ids = (record.findingVersions ?? []).map((pinned) => pinned.id);
  const current = ids.filter((id) => exportable.has(id));
  const left = ids.length - current.length;
  const failed = record.status === 'failed';
  const pending = record.status === 'pending';
  const downloadable = !record.stale && !failed && !pending;

  return (
    <li className={`export${record.stale ? ' export--stale' : ''}`}>
      <div className="export__body">
        <p className="export__title">
          {record.title} <span className="tag">{record.format.toUpperCase()}</span>
          {record.stale && <span className="tag tag--gap">Stale — do not share</span>}
          {failed && <span className="tag tag--gap">Failed</span>}
          {pending && <span className="tag">Generating</span>}
        </p>
        <p className="muted small">
          Created {formatDateTime(record.createdAt)}
          {record.sourceCutoff !== undefined && ` · sources to ${formatDate(record.sourceCutoff)}`}
          {record.findingVersions !== undefined && ` · ${plural(record.findingVersions.length, 'finding')}`}
        </p>
        {record.stale && (
          <p className="small export__stale-note" role="note">
            {record.staleReason ??
              'A finding it includes was corrected, challenged or re-reviewed, or someone changed their attribution preference.'}{' '}
            Its download is withheld. Generate a new export from the current reviewed versions instead.
          </p>
        )}
        {record.stale && left > 0 && (
          <p className="small muted">
            {plural(left, 'included finding')} {left === 1 ? 'is' : 'are'} not currently reviewed and unflagged, so a new export leaves{' '}
            {left === 1 ? 'it' : 'them'} out until {left === 1 ? 'it is' : 'they are'} reviewed again.
          </p>
        )}
        <MutationError error={regenerate.error} conflictMessage={STALE_CONFLICT} />
        {regenerated && (
          <p className="form-success" role="status">
            A new “{record.title}” was requested from the current reviewed versions.
          </p>
        )}
      </div>
      <div className="export__actions">
        {downloadable && (
          <a className="button button--secondary" href={exportDownloadUrl(record.id)} aria-label={`Download ${record.title}`}>
            Download
          </a>
        )}
        {record.stale && current.length > 0 && !regenerated && (
          <button
            type="button"
            className="button button--primary"
            aria-label={`Regenerate ${record.title}`}
            disabled={regenerate.isPending}
            onClick={() =>
              regenerate.mutate({ title: record.title, format: record.format, findingIds: current }, { onSuccess: () => setRegenerated(true) })
            }
          >
            {regenerate.isPending ? 'Regenerating…' : left > 0 ? `Regenerate with ${plural(current.length, 'finding')}` : 'Regenerate'}
          </button>
        )}
        {record.stale && current.length === 0 && (
          <p className="small muted">None of its findings are currently exportable. Review them again first.</p>
        )}
      </div>
    </li>
  );
}

export function ExportComposer() {
  const findings = useAllFindings({ status: 'reviewed' });
  const exports = useExports();
  const exportable = new Set((findings.data?.items ?? []).filter(isExportable).map((finding) => finding.id));
  return (
    <main id="main" tabIndex={-1} className="page">
      <header className="page__header">
        <p className="eyebrow">Stewardship</p>
        <h1 className="page__title">Exports</h1>
        <p className="lede">
          Share reviewed findings as an editable deck, a PDF or a substantial Markdown companion. Each export pins the exact
          versions it used and is marked stale when any of them change.
        </p>
      </header>
      <StewardNav />
      <QueryState query={findings} label="reviewed findings">
        {(page) => <Composer findings={page.items} />}
      </QueryState>
      <section className="finding__section" aria-labelledby="exports-title">
        <h2 id="exports-title" className="section-title">
          Earlier exports
        </h2>
        <QueryState query={exports} label="exports">
          {(page) =>
            page.items.length === 0 ? (
              <p className="muted">No exports yet.</p>
            ) : (
              <ul className="export-list">
                {page.items.map((record) => (
                  <ExportRow key={record.id} record={record} exportable={exportable} />
                ))}
              </ul>
            )
          }
        </QueryState>
      </section>
      <p className="muted small">
        Statuses shown: <StatusBadge status="reviewed" /> findings only. Proposed, contested and superseded findings never enter an export.
      </p>
    </main>
  );
}
